import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import { fetchJson } from '@atlassian/jira-fetch/src/index.tsx';
import { createLocalExpirableStorageProvider } from '@atlassian/jira-browser-storage-providers/src/controllers/local-storage/index.tsx';
import { fireOperationalAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { AccessRequestCapabilityResponse } from './types.tsx';

const fetchUserCanRequestAccess = async (cloudId: string): Promise<boolean> => {
	const resource = `ari:cloud:confluence::site/${cloudId}`;
	const params = new URLSearchParams({
		resource,
	});
	const URL = `/gateway/api/invitations/v1/access-requests/capabilities?${params.toString()}`;

	const response = await fetchJson<AccessRequestCapabilityResponse>(URL, {
		credentials: 'include',
	});
	const requestCapabilities = response.results[resource];
	return requestCapabilities === 'DIRECT_ACCESS' || requestCapabilities === 'REQUEST_ACCESS';
};

const CONFLUENCE_CAN_REQUEST_ACCESS_CACHE_KEY =
	'confluence-availability-exp.confluence-capabilities.cache.key';
// Cache for 1 day
const CONFLUENCE_CAN_REQUEST_ACCESS_CACHE_KEY_EXPIRY_MS = 24 * 60 * 60 * 1000;
const confluenceCanRequestAccessApiCache = createLocalExpirableStorageProvider(
	'confluence-availability-exp-capabilities-api-cache',
);

export const fetchCachedUserCanRequestAccess = async (
	cloudId: string,
	createAnalyticsEvent: CreateUIAnalyticsEvent,
): Promise<boolean | null> => {
	let canRequestAccess = confluenceCanRequestAccessApiCache.get(
		CONFLUENCE_CAN_REQUEST_ACCESS_CACHE_KEY,
	);

	if (canRequestAccess === undefined) {
		try {
			canRequestAccess = await fetchUserCanRequestAccess(cloudId);
		} catch (error) {
			fireOperationalAnalytics(
				createAnalyticsEvent({}),
				'confAvailabilityInSearchExpRequestCapabilities failed',
				{
					errorAction: 'Failed to fetch user capabilities',
					error,
				},
			);
		} finally {
			// Set the cache either way. This make sure if error occurs, we don't keep hitting the API.
			setIsRACapableCache(canRequestAccess ?? false, createAnalyticsEvent);
		}
	}
	return canRequestAccess ?? false;
};

const setIsRACapableCache = (value: boolean, createAnalyticsEvent: CreateUIAnalyticsEvent) => {
	try {
		confluenceCanRequestAccessApiCache.set(
			CONFLUENCE_CAN_REQUEST_ACCESS_CACHE_KEY,
			value,
			Date.now() + CONFLUENCE_CAN_REQUEST_ACCESS_CACHE_KEY_EXPIRY_MS,
		);
	} catch (error) {
		fireOperationalAnalytics(
			createAnalyticsEvent({}),
			'confAvailabilityInSearchExpRequestCapabilities failed',
			{
				errorAction: 'Failed to set user capabilities in cache',
				error,
			},
		);
	}
};
